import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../ui/Modal';
import { HelpIcon } from '../../ui/HelpIcon';
import { adminClientsService } from '../../../services/adminClientsService';
import { toastSuccess, toastError } from '../../../lib/toast';

const ROLE_TOOLTIP =
  "Analyste : utilisateur métier (upload, extraction, validation).\nAdmin client : analyste + gestion du tenant (users, domaines, storage).";

interface UserOption {
  id: string;
  email: string;
  username: string;
  full_name?: string;
}

interface Props {
  open: boolean;
  clientId: string;
  onClose: () => void;
  onAdded: () => void;
}

const ROLES = [
  { value: 'analyste', label: 'Analyste' },
  { value: 'admin_client', label: 'Admin Client' },
];

// PROD1.4.6 #483 — validation UUID côté UI avant submit.
export const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isValidUUID = (s: string): boolean => UUID_REGEX.test(s.trim());

// PROD1.4.8 #529 : la création de nouveaux comptes passe désormais
// exclusivement par Paramètres > Utilisateurs. Cette modale ne rattache
// que des comptes EXISTANTS.
export const AddUserModal: React.FC<Props> = ({ open, clientId, onClose, onAdded }) => {
  const [submitting, setSubmitting] = useState(false);

  const [userId, setUserId] = useState('');
  const [existingRole, setExistingRole] = useState('analyste');
  const [allUsers, setAllUsers] = useState<UserOption[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setUsersLoading(true);
    adminClientsService
      .searchUsers()
      .then((users) => {
        if (!cancelled) setAllUsers(users);
      })
      .catch(() => {
        if (!cancelled) toastError('Impossible de charger la liste des utilisateurs');
      })
      .finally(() => {
        if (!cancelled) setUsersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const filteredUsers = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return allUsers.slice(0, 20);
    return allUsers
      .filter((u) =>
        u.email.toLowerCase().includes(q) ||
        u.username.toLowerCase().includes(q) ||
        (u.full_name?.toLowerCase().includes(q) ?? false),
      )
      .slice(0, 20);
  }, [allUsers, userQuery]);

  const selectUser = (u: UserOption) => {
    setSelectedUser(u);
    setUserId(u.id);
    setUserQuery(`${u.email} (${u.username})`);
    setShowDropdown(false);
  };

  const reset = () => {
    setUserId('');
    setExistingRole('analyste');
    setUserQuery('');
    setSelectedUser(null);
    setShowDropdown(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId.trim() || !selectedUser) {
      toastError('Sélectionnez un utilisateur dans la liste');
      return;
    }
    if (!isValidUUID(userId)) {
      toastError('Format UUID invalide (36 caractères hexadécimaux séparés par des tirets)');
      return;
    }
    setSubmitting(true);
    try {
      await adminClientsService.addUserExisting(clientId, userId.trim(), existingRole);
      toastSuccess('Utilisateur rattaché');
      reset();
      onAdded();
      onClose();
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        toastError('Utilisateur déjà rattaché à ce client');
      } else if (status === 422) {
        toastError('Données invalides', err);
      } else {
        toastError('Échec du rattachement', err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={open} onClose={handleClose} title="Rattacher un utilisateur existant" size="md">
      <div className="space-y-4">
        <div className="text-xs text-gray-500">
          La création d'un nouveau compte se fait dans{' '}
          <span className="font-medium">Paramètres &gt; Utilisateurs</span>. Cette modale ne rattache que des comptes existants.
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <Field label="Utilisateur" required>
            <div className="relative">
              <input
                type="text"
                value={userQuery}
                onChange={(e) => {
                  setUserQuery(e.target.value);
                  setSelectedUser(null);
                  setUserId('');
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                placeholder={usersLoading ? 'Chargement...' : 'Email ou username...'}
                required
                disabled={usersLoading}
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
              />
              {showDropdown && filteredUsers.length > 0 && (
                <div className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-200 rounded shadow-lg">
                  {filteredUsers.map((u) => (
                    <button
                      type="button"
                      key={u.id}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectUser(u)}
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-blue-50"
                    >
                      <div className="font-medium text-gray-900">{u.email}</div>
                      <div className="text-xs text-gray-500">
                        {u.username}
                        {u.full_name ? ` — ${u.full_name}` : ''}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {showDropdown && !usersLoading && filteredUsers.length === 0 && userQuery.trim() && (
                <div className="absolute z-10 mt-1 w-full px-3 py-2 bg-white border border-gray-200 rounded shadow-lg text-sm text-gray-500">
                  Aucun utilisateur trouvé
                </div>
              )}
            </div>
            {selectedUser && (
              <div className="mt-1 text-xs text-gray-500 font-mono">{selectedUser.id}</div>
            )}
            {userId && !isValidUUID(userId) && (
              <div className="mt-1 text-xs text-red-600" role="alert">
                Format UUID invalide (36 caractères hexadécimaux séparés par des tirets)
              </div>
            )}
          </Field>
          <Field label="Rôle" required helpContent={ROLE_TOOLTIP}>
            <select
              value={existingRole}
              onChange={(e) => setExistingRole(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm bg-white"
            >
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </Field>
          <FormActions
            onCancel={handleClose}
            submitting={submitting}
            disabled={!!userId && !isValidUUID(userId)}
          />
        </form>
      </div>
    </Modal>
  );
};

const Field: React.FC<{
  label: string;
  required?: boolean;
  helpContent?: string;
  children: React.ReactNode;
}> = ({ label, required, helpContent, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
      {helpContent && <HelpIcon content={helpContent} />}
    </label>
    {children}
  </div>
);

const FormActions: React.FC<{ onCancel: () => void; submitting: boolean; disabled?: boolean }> = ({
  onCancel,
  submitting,
  disabled,
}) => (
  <div className="flex justify-end gap-2 pt-2">
    <button
      type="button"
      onClick={onCancel}
      disabled={submitting}
      className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
    >
      Annuler
    </button>
    <button
      type="submit"
      disabled={submitting || disabled}
      className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50"
    >
      {submitting ? 'Envoi...' : 'Rattacher'}
    </button>
  </div>
);

export default AddUserModal;
